import { TableRow } from './table-models';
import { MaterialBranch } from './dashboard-models';

export interface Material {
    id: number;
    passportId: string;
    name: string;
    type: string;
}

export interface Battery extends Material {
    model: string;
    batteryVoltage: number;
    batteryCapacity: number;
}

export interface Oil extends Material {
    oilType: string;
    oilCapacity: number;
    trackingType?: ConsumableTrackingType;
}

export interface Device extends Material {
    model: string;
    power: number;
    hasRemoteControl: boolean;
    areaCoverage: string;
    sprayRange: number;
    waitRange: number;
    resources: {
        id: number;
        oilConsumption: number;
        batteryConsumptionOn: number;
        batteryConsumptionStandBy: number;
        batteryCount: number;
        batteryModel: string;
        oilName: string;
    };
    active?: boolean;
    oilName?: string;
}

export interface Batch extends TableRow {
    passportName: string;
    batchCode: string;
    userId: number;
    cost: number;
    deliveryDate: Date;
    dateTime: Date;
    warehouseName: string;
    isDefective: boolean;
    deviceList: ItemQuantity[];
}

export interface InventoryContent extends TableRow {
    oilFill?: DeviceOilFill;
    passport: string;
    idOfPassport: number;
    type?: PassportItemType;
    item?: PassportItemType;
    count: number;
    forBranch: string;
    state: string;
    batchCode: string;
    cost: number;
    availableAmount?: number;
    totalAmount?: number;
    collectionOfItems?: any[];
    trackingType?: ConsumableTrackingType;
    hasContainer?: boolean;
}

export interface DeviceOilFill {
    taskDeviceId: number;
    devicePassportId: number;
    sources: OilFillSource[];
}

export interface OilFillSource {
    inventoryId: number;
    oilPassportId: number;
    batchCode: string;
    amountMl: number;
    installedBottle: boolean;
}

export interface InventoryContentForTask {
    id: number;
    contentId: number;
    taskId: number;
    passportId: number;
    count: number;
    batchCode: string;
    cost: number;
    warehouseId: number;
    state: ItemState;
    userId?: number;
}

export interface Handout extends InventoryContent {
    forUser: string;
    quantityBatch: QuantityCount;
}

export interface Scrap extends TableRow {
    passport: string;
    batchCode: string;
    count: number;
    warehouseId: number;
    distributor: string;
    arrivalDate: Date;
}

export interface ManualHandout extends Handout {
    forUserId?: number;
    passportId: PassportId;
    type?: PassportItemType;
}

export interface CompletedHandout extends TableRow {
    taskId: number;
    passportId: number;
    price: number;
    name: string;
    batchCode: string;
    warehouse: string;
    branch: string;
    client: string;
    receivingUsername: string;
    warehouseUsername: string;
    handoutDate: Date;
    itemCount: number;
    clientId: number;
    branchId: number;
    details: string;
    distributorName?: string;
    receivingUserId: number;
    warehouseUserId: number;
    state?: boolean;
}

export interface CompletedHandoutGroup extends TableRow {
    date: string;
    distributors: CompletedHandoutDistributorGroup[];
    totalCount: number;
}

export interface CompletedHandoutDistributorGroup extends TableRow {
    distributorName: string;
    items: CompletedHandout[];
    receivingUserId: number;
    receivingUsername: string;
    totalCount: number;
}

export type HandoutRequestStatus = 'PENDING' | 'PREPARING' | 'APPROVED' | 'DECLINED' | 'PARTIALLY_APPROVED';
export type HandoutRequestSource = 'TASK' | 'MANUAL';
export type HandoutRequestPurpose = 'SERVICE' | 'SALE';

export interface HandoutRequestItem extends TableRow {
    itemId?: number;
    passportId: number;
    passportName: string;
    itemName: string;
    itemType: PassportItemType;
    itemCount: number;
    deviceId: number;
    oilId: number;
    oilName: string;
    oilQuantity: number;
    oilCost: number;
    cost: number;
    status: HandoutRequestStatus;
    requestSource?: HandoutRequestSource;
    requestPurpose?: HandoutRequestPurpose;
    coversItemId?: number | null;
    coversItemName?: string | null;
    coversItemType?: PassportItemType | null;
    coversItemCount?: number | null;
    coversOilId?: number | null;
    coversOilName?: string | null;
    coversOilQuantity?: number | null;
    approvedReceiptId: number | null;
    resolvedAt: string | null;
}

export interface HandoutRequest extends TableRow {
    userId: number;
    distributorName: string;
    warehouseId: number;
    days: number;
    fromDate: string;
    toDate: string;
    status: HandoutRequestStatus;
    createdAt: string;
    updatedAt: string;
    resolvedAt: string | null;
    version: number | null;
    items: HandoutRequestItem[];
}

export interface HandoutPreparationAllocation {
    requestItemId: number;
    reservationId: number;
    quantityId: number;
    passportId: number;
    warehouseId: number;
    batchCode?: string;
    count: number;
}

export interface HandoutPreparation {
    request: HandoutRequest;
    preparationToken: string;
    expiresAt: string;
    allocations: HandoutPreparationAllocation[];
}

/**
 * Cheap staleness marker for the handout request list. Polled instead of the full listing: the
 * table reloads only when one of the two values moves. lastUpdatedAt covers changes the request
 * count cannot show - an edited item count, a dropped position, an item merged into an existing
 * request - and is null when nothing matches the filter.
 */
export interface HandoutRequestsRevision {
    totalCount: number;
    lastUpdatedAt: string | null;
}

export interface ItemQuantity extends TableRow {
    physicalCount?: number;
    passport: PassportId;
    batchCode: string;
    count: number;
    availableAmount?: number;
    user: string;
    cost: number;
    deliveryDate: Date;
    dateTime: Date;
    item?: string;
    warehouseId?: number;
    sapCode: string;
    equipment: string;
    description: string;
    state?: ItemState;
    selectedCount?: number;
    collectionOfItems?: any[];
    hasContainer?: boolean;
    trackingType?: ConsumableTrackingType;
    oilCapacity?: number;
}

export interface RedistributeItemQuantityRequest {
    id: number;
    warehouseId: number;
    count: number;
}

export type ItemState = 'AVAILABLE' | 'USED' | 'BROKEN' | 'DEFECTIVE' | 'EMPTY';

export interface PassportType {
    type: string;
    passports: PassportId[];
}

export interface PassportId {
    passportId: number;
    passport: string;
    name: string;
}

export interface QuantityCountList {
    isDevice: boolean;
    quantityCounts: QuantityCount[];
}

export interface QuantityCount {
    quantityId: number;
    passportId: number;
    count: number;
    batchCode: string;
    countToBeExtracted: number;
    warehouseId: string;
    hasContainer?: boolean;
}

export interface HandoutReservationRequest {
    reservationId?: number;
    quantityId: number;
    passportId: number;
    warehouseId: number;
    batchCode: string;
    count: number;
    forUserId?: number;
}

export interface HandoutReservationResponse {
    reservationId: number;
    quantityId: number;
    passportId?: number;
    warehouseId?: number;
    batchCode?: string;
    reservedCount: number;
    availableCount?: number;
    forUserId?: number;
    expiresAt: string;
}

export interface HandoutReservationErrorResponse {
    availableCount?: number;
    error?: string;
    errorCode?: string;
}

export interface ManualHandoutQuantityBatch {
    id?: number;
    quantityId?: number;
    passportId: number;
    count: number;
    countToBeExtracted?: number;
    batchCode?: string;
    warehouseId?: string | number;
    reservationId?: number;
    hasContainer?: boolean;
}

export interface ManualHandoutRequest {
    passportId?: number;
    quantityBatch: ManualHandoutQuantityBatch;
    forUserId?: number;
    forUser?: string;
    type?: string;
    forSale?: boolean;
    requestSource?: HandoutRequestSource;
    requestPurpose?: HandoutRequestPurpose;
}

export interface WeekDays {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
}

export interface WorkHours {
    id?: number;
    deviceId?: number;
    sprayRange: number;
    waitRange: number;
    startTime: string;
    endTime: string;
    weekDaysBoolean: WeekDays;
}

export interface BranchDevice {
    id?: number;
    deviceId?: number;
    taskDeviceId?: number;
    branchId?: number;
    inventoryNumber?: string;
    batchNumber?: string;
    warehouseDeviceId?: number;
    deviceName: string;
    model: string;
    fullModelName?: string | null;
    oilName: string;
    oilType: string;
    active?: false;
    batteryPercent?: number;
    batteryRemainingDays?: number;
    batteryState?: number;
    maintenanceRemainingDays?: number;
    maintenanceState?: number;
    oilId?: number;
    oilPercent?: number;
    oilRemainingDays?: number;
    oilState?: number;
    passportId: number;
    shouldHasBattery?: boolean;
    batteryId?: number;
    batteryName?: string;
    workHoursList: WorkHours[];
    materials?: MaterialBranch[];
    hasContainer?: boolean;
}

export interface BranchMaterial {
    passportId?: number;
    itemCount?: number;
}

export interface ShortOilPassport {
    type: string;
    name: string;
    oilId: number;
    oilType: string;
    oilCapacity: number;
    price: number;
    trackingType?: ConsumableTrackingType;
}

export interface ShortDevicePassport {
    id?: number;
    passportId: string;
    name: string;
    model: string;
    type: PassportItemType;
    oilType: string;
    oilCapacity?: number;
    trackingType?: ConsumableTrackingType;
    sprayRange: number;
    waitRange: number;
    imgs: [];
    oils: ShortOilPassport[];
    requireBattery: boolean;
    supportedContainerType?: ConsumableContainerType | null;
    containerType?: ConsumableContainerType | null;
    capacityMl?: number;
    emptyContainerPassportId?: number | null;
    emptyContainerPassportName?: string | null;
    serviceable?: boolean;
    servicePeriodMonths?: number;
    serviceConsumablePassportId?: number;
    serviceConsumableCount?: number;
}

export interface Passport {
    id: number;
    name: string;
    type: string;
    archived?: boolean;
    price: number;
    priceForInvoice: number;
    priceForSale: number;
    essencePriceGroupId?: number;
    deviceGroupId?: number;
    reedInstallationGroupId?: number;
    consumableServiceGroupId?: number;
    serviceable?: boolean;
    servicePeriodMonths?: number;
    serviceConsumablePassportId?: number;
    serviceConsumableCount?: number;
}

export const PassportType = ['DEVICE', 'OIL', 'BATTERY', 'CONSUMABLE', 'CONTAINER', 'OTHER'];
export type PassportItemType = 'DEVICE' | 'OIL' | 'BATTERY' | 'CONSUMABLE' | 'CONTAINER' | 'OTHER';

export interface ChildPassportLite {
    id: number;
    name: string;
    forDeviceModel?: string;
    isUniversal?: boolean;
}

export interface AnyPassport extends Passport {
    minimumDeposit?: number;
    images?: File[];
    model?: string;
    power?: number;
    hasRemoteControl?: boolean;
    areaCoverage?: string;
    sprayRange?: number;
    waitRange?: number;
    sapCode?: string;
    resources?: DevicePassportResources;
    oilCapacityType: string;
    batteryVoltage?: number;
    batteryCapacity?: number;

    oilType?: string;
    oilCapacity?: number;
    trackingType?: ConsumableTrackingType;
    activeSubstanceWeightGrams?: number;
    emptyPackageWeightGrams?: number;
    forDeviceModel?: string;
    isUniversal?: boolean;
    childPassportIds?: number[];
    childPassports?: ChildPassportLite[];

    containerType?: ConsumableContainerType | null;
    capacityMl?: number;

    forModel?: string;

    description?: string;
}

export interface DevicePassportResources {
    id: number;
    oilType: string;
    oilConsumption: number;
    oilCapacity: number;
    oilConsumptionPerSecond: number;
    batteryConsumptionOn?: number;
    batteryConsumptionStandBy?: number;
    batteryCount?: number;
    batteryModel?: string;
    requireBattery?: boolean;
    supportedContainerType?: ConsumableContainerType | null;
    emptyContainerPassportId?: number | null;
    emptyContainerPassportName?: string | null;
}

export interface DevicePassport extends Passport {
    minimumDeposit: number;
    model: string;
    power: number;
    hasRemoteControl: boolean;
    areaCoverage: string;
    sprayRange: number;
    waitRange: number;
    sapCode: string;
    resources: DevicePassportResources;
}

export interface BatteryPassport extends Passport {
    model: string;
    batteryVoltage: number;
    batteryCapacity: number;
}

export interface OilPassport extends Passport {
    model: string;
    oilType: string;
    oilCapacity: number;
    forDeviceModel: string | null;
    isUniversal?: boolean;
    childPassportIds?: number[];
    childPassports?: OilPassport[];
    trackingType?: ConsumableTrackingType;
    activeSubstanceWeightGrams?: number;
    emptyPackageWeightGrams?: number;
}

export type ConsumableTrackingType = 'LIQUID_ML' | 'SPRAY_WEIGHT';
export type ConsumableContainerType = 'BOTTLE' | 'SPRAY_CAN';

export interface ConsumablePassport extends Passport {
    forModel: string;
}

export interface RequestMaterial {
    id: number;
    createdOn: string;
    passportId: number;
    itemName: string;
    itemType: PassportType;
    itemCount: number;
}

export interface HandoverItem {
    itemId: number;
    itemName: string;
    itemType: 'DEVICE' | 'BATTERY' | 'OIL' | 'CONSUMABLE' | 'CONTAINER' | 'OTHER';
    itemCount: number;
    oilName: string;
    oilQuantity: number;
    cost?: number;
}

export interface HandoverProps {
    id?: number;
    date: string;
    town: string;
    representative: string;
    taskItemsVO: {
        branchId: number;
        userId: number;
        neededItems: HandoverItem[];
        taskId: number;
        taskCategory: string;
    };
    save?: boolean;
    signature?: string;
}

export interface RentalAgreementProps {
    branchId?: number;
    models?: string[];
    reason?: string;
    inCash?: boolean;
}

interface WarehouseReceiptItems {
    itemName: string;
    amount: number;
    currency: string;
    price: number;
}

export interface WarehouseReceiptProps {
    id: number;
    date: string;
    town: string;
    taskId?: number;

    branchId: number;
    userId: number;
    creator: string;
    items: WarehouseReceiptItems[];
}

export interface HandoversSummary {
    newDevicesByModel?: Record<string, number>;
    servicedDevicesByModel?: Record<string, number>;
    returnedDevicesByModel?: Record<string, number>;
}
